import { useState } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw } from 'lucide-react';
import { rejectionDetail } from '@/lib/api/errors';
import { helpdeskApi, type HelpdeskConfig } from '@/lib/api/helpdesk';

const INPUT = 'mt-1 block w-full px-4 py-2 bg-white dark:bg-slate-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';
const LABEL = 'text-sm font-medium text-gray-700 dark:text-gray-300';
const FAILED = 'Unable to save the new credentials. Please try again.';

/**
 * Re-authorise an existing integration in place.
 *
 * Exchanges a fresh grant token and stores the resulting credentials, so an
 * expired connection can be restored without deleting the integration and its
 * cached tickets. Rotated client secrets are saved here too.
 */
export function ReconnectModal({
  config,
  onClose,
  onSuccess,
}: {
  config: HelpdeskConfig;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [grantToken, setGrantToken] = useState('');
  const [region, setRegion] = useState(config.region || 'eu');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const complete = Boolean(clientId && clientSecret && grantToken);

  const reconnect = async () => {
    setBusy(true);
    setError('');
    try {
      const tokens = await helpdeskApi.exchangeCode({
        provider: config.provider,
        client_id: clientId,
        client_secret: clientSecret,
        code: grantToken,
        grant_type: 'authorization_code',
        region,
      });
      if (!tokens.success || !tokens.access_token) {
        setError(tokens.message || tokens.error || 'Token exchange failed');
        return;
      }
      const expiresAt = new Date(Date.now() + ((tokens.expires_in ?? 3600) - 60) * 1000).toISOString();
      await helpdeskApi.updateConfig(config.id, {
        client_id: clientId,
        client_secret: clientSecret,
        region,
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
        token_expires_at: expiresAt,
      });
      onSuccess();
    } catch (rejection) {
      setError(rejectionDetail(rejection) || FAILED);
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
        className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={event => event.stopPropagation()}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Reconnect {config.name}</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm">
            Generate a new grant token, then enter it with the integration's credentials. Cached tickets and
            customer links are kept.
          </p>
        </div>

        <div className="p-6 space-y-4">
          <label className="block">
            <span className={LABEL}>Client ID</span>
            <input type="text" value={clientId} className={INPUT} placeholder="Enter OAuth Client ID"
              onChange={event => setClientId(event.target.value)} />
          </label>
          <label className="block">
            <span className={LABEL}>Client Secret</span>
            <input type="password" value={clientSecret} className={INPUT} placeholder="Enter OAuth Client Secret"
              onChange={event => setClientSecret(event.target.value)} />
          </label>
          <label className="block">
            <span className={LABEL}>Grant Token</span>
            <input type="text" value={grantToken} className={INPUT} placeholder="1000.xxxxxxxxxxxxxxxxxxxx"
              onChange={event => setGrantToken(event.target.value)} />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Generate from the <a href="https://api-console.zoho.eu/" target="_blank" rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:underline">Zoho API Console</a> with scope:
              Desk.basic.READ,profile.userphoto.READ,Desk.tickets.READ,Desk.contacts.READ
            </p>
          </label>
          <label className="block">
            <span className={LABEL}>Region</span>
            <select value={region} className={INPUT} onChange={event => setRegion(event.target.value)}>
              <option value="eu">Europe (EU)</option>
              <option value="us">United States (US)</option>
              <option value="in">India (IN)</option>
              <option value="au">Australia (AU)</option>
              <option value="cn">China (CN)</option>
              <option value="jp">Japan (JP)</option>
            </select>
          </label>

          {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={busy}
              className="flex-1 px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-50 transition-colors">
              Cancel
            </button>
            <button type="button" onClick={reconnect} disabled={busy || !complete}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center">
              {busy ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Reconnecting...</>) : 'Reconnect'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
